import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { Clock, Truck, TrendingUp, ArrowLeft } from 'lucide-react';
import { useDriverPerformance } from '@/hooks/useDriverPerformance';
import { DailyCallsChart } from './components/DailyCallsChart';

// Utility function to format time in hours and minutes
const formatTimeDisplay = (
  minutes: number
): { value: string; unit: string } => {
  if (minutes <= 60) {
    return {
      value: Math.round(minutes).toString(),
      unit: 'minutes',
    };
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.round(minutes % 60);

  if (remainingMinutes === 0) {
    return {
      value: `${hours}h`,
      unit: '',
    };
  }

  return {
    value: `${hours}h ${remainingMinutes}m`,
    unit: '',
  };
};

interface DriverPerformanceScreenProps {
  driverId: string;
  driverName: string;
  onBack: () => void;
}

export function DriverPerformanceScreen({ driverId, driverName, onBack }: DriverPerformanceScreenProps) {
  // Calculate date range for past 7 days - memoized to prevent unnecessary re-renders
  const { startDate, endDate, daysDifference } = useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 7);

    // Calculate actual number of days between start and end
    const timeDifference = end.getTime() - start.getTime();
    const daysDiff = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));

    return {
      startDate: start.toISOString(),
      endDate: end.toISOString(),
      daysDifference: daysDiff,
    };
  }, []); // Empty dependency array - this should only be calculated once

  const { data: performanceMetrics, isLoading } = useDriverPerformance(
    driverId,
    {
      startDate,
      endDate,
    }
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" style={{ backgroundColor: '#f9fafb' }}>
        <div className="text-center">
          <Spinner className="size-8 mx-auto mb-4" />
          <p className="text-gray-600">Loading performance data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" style={{ backgroundColor: '#f9fafb' }}>
      {/* Header */}
      <div className="bg-background border-b border-gray-200 px-4 py-4" style={{ backgroundColor: '#ffffff', borderColor: '#e5e7eb' }}>
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-black">{driverName}</h1>
            <p className="text-sm" style={{ color: '#4b5563' }}>Driver Performance</p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-6">
        <div className="grid gap-6 md:grid-cols-3">
          {/* Average Response Time */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2" style={{ color: '#000000' }}>
                <Clock className="h-5 w-5" />
                <span>Average Response Time</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold" style={{ color: '#000000' }}>
                {
                  formatTimeDisplay(performanceMetrics?.averageResponseTime || 0)
                    .value
                }
              </div>
              <p className="text-sm text-gray-500">
                {
                  formatTimeDisplay(performanceMetrics?.averageResponseTime || 0)
                    .unit
                }
              </p>
            </CardContent>
          </Card>

          {/* Total Completed Calls */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2" style={{ color: '#000000' }}>
                <Truck className="h-5 w-5" />
                <span>Completed Calls</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold" style={{ color: '#000000' }}>
                {performanceMetrics?.totalCompletedCalls || 0}
              </div>
              <p className="text-sm text-gray-500">in the last 7 days</p>
            </CardContent>
          </Card>

          {/* Performance Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2" style={{ color: '#000000' }}>
                <TrendingUp className="h-5 w-5" />
                <span>Performance Summary</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">
                    Calls per day:
                  </span>
                  <span className="font-medium" style={{ color: '#000000' }}>
                    {performanceMetrics?.totalCompletedCalls && daysDifference > 0
                      ? (
                          performanceMetrics.totalCompletedCalls / daysDifference
                        ).toFixed(2)
                      : '0.00'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">
                    Avg response:
                  </span>
                  <span className="font-medium" style={{ color: '#000000' }}>
                    {
                      formatTimeDisplay(
                        performanceMetrics?.averageResponseTime || 0
                      ).value
                    }
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Daily Calls Chart */}
        <DailyCallsChart
          dailyMetrics={performanceMetrics?.dailyMetrics}
          isLoading={isLoading}
        />

        {/* Performance Notes */}
        <Card>
          <CardHeader>
            <CardTitle style={{ color: '#000000' }}>Performance Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="text-sm text-gray-600">
                <p>
                  <strong>Response Time:</strong> Average time from job assignment
                  to completion. Lower times indicate better efficiency.
                </p>
              </div>
              <div className="text-sm text-gray-600">
                <p>
                  <strong>Completed Calls:</strong> Total number of successfully
                  completed jobs in the last 7 days.
                </p>
              </div>
              <div className="text-sm text-gray-600">
                <p>
                  <strong>Performance Summary:</strong> Daily averages and key
                  metrics to help assess driver efficiency and reliability.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
